/**
 * =============================================================================
 * RFI ITEM ROW COMPONENT
 * =============================================================================
 *
 * Renders one RFI item (already ordinal-sorted by the caller): label, required
 * flag, review-status badge, type metadata, its linked responses (with a
 * graceful "Linked reply r_…" fallback when the reply body isn't in the
 * candidate cache), per-response unlink, and the per-item review control.
 *
 * Two-zone gating (D-8): the review control NEVER offers `OPEN` (system-derived)
 * and is disabled until the item has ≥1 linked response (`responses.length`).
 * `COMPLETE` gets a lightweight inline confirm because it can roll the RFI up to
 * ANSWERED. Terminal (SUPERSEDED/CANCELLED) RFIs render read-only.
 *
 * =============================================================================
 */

import React, { useState } from 'react';
import { Box, Button, Flex, FlexItem, Text } from '@bigcommerce/big-design';
import type { AdminReplyListItem, AdminRfiItem } from '@find-my-part/types';
import { formatDate } from '../../../utils/formatters';
import type { SettableReviewStatus } from '../../../hooks/useRfi';
import {
  buildReplyExcerpt,
  RFI_ITEM_REVIEW_STATUS_LABELS,
  RFI_ITEM_REVIEW_STATUS_VARIANTS,
  RFI_ITEM_TYPE_LABELS,
} from './rfiLabels';
import { tokens } from '../../../tokens';
import { StatusChip } from '../../common/StatusChip';

/** Settable review states, in display order. `OPEN` is intentionally absent. */
const SETTABLE_REVIEW_STATUSES: SettableReviewStatus[] = [
  'UNREVIEWED',
  'INSUFFICIENT',
  'PARTIAL',
  'COMPLETE',
];

/**
 * Props for RfiItemRow.
 */
export interface RfiItemRowProps {
  /** The RFI item to render. */
  item: AdminRfiItem;
  /** Whether the owning RFI is terminal (read-only — no review/unlink). */
  isReadOnly: boolean;
  /** Hook pending-key set (review:${rfiItemId}, unlink:${rfiResponseId}). */
  pendingActions: Set<string>;
  /**
   * When true, this item's mutating controls (review + unlink) are disabled even
   * if their own per-action key isn't pending — used by the parent to freeze the
   * whole RFI while a terminal CANCEL is in flight on it (no point reviewing/
   * unlinking an RFI that is being withdrawn).
   */
  disabled?: boolean;
  /** Set this item's review status (bound to rfiId + rfiItemId by the parent). */
  onReview: (reviewStatus: SettableReviewStatus) => void;
  /** Unlink one response (bound to rfiId by the parent). */
  onUnlink: (rfiResponseId: string) => void;
  /** Candidate replies by id — used to resolve a linked reply's body excerpt. */
  candidateRepliesById: Map<string, AdminReplyListItem>;
  /** Optional test ID prefix. */
  testId?: string;
}

/**
 * One RFI item with its linked responses and review control.
 */
export function RfiItemRow({
  item,
  isReadOnly,
  pendingActions,
  disabled = false,
  onReview,
  onUnlink,
  candidateRepliesById,
  testId,
}: RfiItemRowProps): React.ReactElement {
  const [confirmingComplete, setConfirmingComplete] = useState(false);

  const hasResponses = item.responses.length > 0;
  const isReviewPending = pendingActions.has(`review:${item.rfiItemId}`);
  const reviewDisabled = isReadOnly || !hasResponses || isReviewPending || disabled;

  const handleReviewClick = (status: SettableReviewStatus): void => {
    if (status === 'COMPLETE') {
      setConfirmingComplete(true);
      return;
    }
    onReview(status);
  };

  const handleConfirmComplete = (): void => {
    onReview('COMPLETE');
    setConfirmingComplete(false);
  };

  return (
    <Box
      role="listitem"
      padding="small"
      marginBottom="xSmall"
      style={{ border: `1px solid ${tokens.color.border.default}`, borderRadius: tokens.dimension.radius.md }}
      data-testid={testId}
    >
      {/* Header: label + required + review-status badge */}
      <Flex alignItems="center" justifyContent="space-between" flexGap="0.5rem">
        <FlexItem>
          <Text bold>
            {item.label}
          </Text>
          <Text color="secondary">
            {RFI_ITEM_TYPE_LABELS[item.itemType]}
            {' · '}
            {item.required ? 'Required' : 'Optional'}
            {item.itemType === 'measurement' && item.unit ? ` · Unit: ${item.unit}` : ''}
          </Text>
          {item.itemType === 'selection' && item.options && item.options.length > 0 && (
            <Text color="secondary">
              Options: {item.options.join(', ')}
            </Text>
          )}
        </FlexItem>
        <FlexItem>
          <StatusChip
            label={RFI_ITEM_REVIEW_STATUS_LABELS[item.reviewStatus]}
            variant={RFI_ITEM_REVIEW_STATUS_VARIANTS[item.reviewStatus]}
          />
        </FlexItem>
      </Flex>

      {item.reviewStatus === 'COMPLETE' && item.satisfiedAt && (
        <Text color="secondary">
          Satisfied {formatDate(item.satisfiedAt)}
        </Text>
      )}

      {/* Linked responses */}
      {hasResponses && (
        <Box
          role="list"
          aria-label={`Linked responses for ${item.label}`}
          marginTop="xSmall"
          data-testid={testId ? `${testId}-responses` : undefined}
        >
          {item.responses.map((response) => {
            const cachedReply = candidateRepliesById.get(response.replyId);
            const excerpt = cachedReply
              ? buildReplyExcerpt(cachedReply.body)
              : `Linked reply ${response.replyId}`;
            // Public image attachments carry no filename (ADR-022 §6.1/§9), so
            // the attachment is labelled by its id.
            const attachmentLabel = response.attachmentId ?? null;
            const unlinkPending = pendingActions.has(`unlink:${response.rfiResponseId}`);
            return (
              <Box
                key={response.rfiResponseId}
                role="listitem"
                padding="xSmall"
                marginBottom="xSmall"
                style={{ backgroundColor: tokens.color.surface.subtle, borderRadius: tokens.dimension.radius.sm }}
                data-testid={
                  testId ? `${testId}-response-${response.rfiResponseId}` : undefined
                }
              >
                <Flex alignItems="flex-start" justifyContent="space-between" flexGap="0.5rem">
                  <FlexItem>
                    <Text>{excerpt}</Text>
                    {response.valueText && (
                      <Text color="secondary">
                        Value: {response.valueText}
                      </Text>
                    )}
                    {attachmentLabel && (
                      <Text color="secondary">
                        Attachment: {attachmentLabel}
                      </Text>
                    )}
                    <Text color="secondary">
                      Linked by Staff
                    </Text>
                  </FlexItem>
                  {!isReadOnly && (
                    <FlexItem>
                      <Button
                        variant="subtle"
                        onClick={() => onUnlink(response.rfiResponseId)}
                        disabled={unlinkPending || disabled}
                        isLoading={unlinkPending}
                        aria-label={`Unlink response from ${item.label}`}
                        data-testid={
                          testId
                            ? `${testId}-unlink-${response.rfiResponseId}`
                            : undefined
                        }
                      >
                        Unlink
                      </Button>
                    </FlexItem>
                  )}
                </Flex>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Review control (two-zone gating; never offers OPEN) */}
      {!isReadOnly && (
        <Box marginTop="xSmall">
          {!hasResponses ? (
            <Text
              color="secondary"
              data-testid={testId ? `${testId}-review-disabled-note` : undefined}
            >
              Link evidence before this item can be reviewed.
            </Text>
          ) : (
            <>
              <Flex flexGap="0.25rem" flexWrap="wrap" alignItems="center">
                <FlexItem>
                  <Text color="secondary">
                    Review:
                  </Text>
                </FlexItem>
                {SETTABLE_REVIEW_STATUSES.map((status) => (
                  <FlexItem key={status}>
                    <Button
                      variant={status === item.reviewStatus ? 'primary' : 'secondary'}
                      onClick={() => handleReviewClick(status)}
                      disabled={reviewDisabled || status === item.reviewStatus}
                      data-testid={testId ? `${testId}-review-${status}` : undefined}
                    >
                      {RFI_ITEM_REVIEW_STATUS_LABELS[status]}
                    </Button>
                  </FlexItem>
                ))}
              </Flex>
              {confirmingComplete && (
                <Box
                  marginTop="xSmall"
                  padding="xSmall"
                  style={{ border: `1px solid ${tokens.color.border.default}`, borderRadius: tokens.dimension.radius.sm }}
                  data-testid={testId ? `${testId}-complete-confirm` : undefined}
                >
                  <Text>
                    Mark complete? The item will count as satisfied.
                  </Text>
                  <Flex flexGap="0.25rem" marginTop="xSmall">
                    <FlexItem>
                      <Button
                        variant="primary"
                        onClick={handleConfirmComplete}
                        // Also freeze the already-open confirm while a terminal
                        // cancel is in flight on this RFI (parent `disabled`), so a
                        // stale confirm-complete can't issue a concurrent review
                        // that races the cancel.
                        disabled={isReviewPending || disabled}
                        data-testid={testId ? `${testId}-complete-confirm-yes` : undefined}
                      >
                        Confirm complete
                      </Button>
                    </FlexItem>
                    <FlexItem>
                      <Button
                        variant="subtle"
                        onClick={() => setConfirmingComplete(false)}
                        data-testid={testId ? `${testId}-complete-confirm-no` : undefined}
                      >
                        Cancel
                      </Button>
                    </FlexItem>
                  </Flex>
                </Box>
              )}
            </>
          )}
        </Box>
      )}
    </Box>
  );
}
